import { useEffect, useMemo, useState } from "react";
import { isMobile } from "react-device-detect";
import { useTranslation } from "react-i18next";
import { GrAttachment } from "react-icons/gr";
import { useParams } from "react-router-dom";
import {
  ActionIcon,
  LoadingOverlay,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { useMounted } from "@mantine/hooks";
import { IconSearch, IconX } from "@tabler/icons-react";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { StringParam, useQueryParam, withDefault } from "use-query-params";

import { GeneralTabProps } from "@pages/Patients/Laboratory/components/GeneralTab";
import { StyledGeneralTabTable } from "@pages/Patients/Laboratory/components/GeneralTab/GeneralTab.styled";

import { CustomPagination } from "@components/CustomPagination";

import { formatDateToString } from "@utils/dateValidation";
import { truncateWithEllipsis } from "@utils/truncateText";

import { QP } from "@constants/index";

import { useGetImmunizationsForPatient } from "@hooks/queries/immunization/useGetImmunizationsForPatient";
import { useSyncTableQueryParams } from "@hooks/useSyncTableQueryParams";

import { Immunization } from "@customTypes/immunization";

import { TableControls } from "../TableControls";


const columnHelper = createColumnHelper<Partial<Immunization>>();

export const GeneralTab = ({ setIsRefreshing }: GeneralTabProps) => {
  const { t, i18n } = useTranslation();

  const { patientId } = useParams();
  const [searchFilter, setSearchFilter] = useQueryParam(
    QP.SEARCH,
    withDefault(StringParam, "")
  );
  const { debounced, tablePagination, setTablePagination } =
    useSyncTableQueryParams({ searchFilter });
  const [tableData, setTableData] = useState<Partial<Immunization>[] | []>([]);

  const [isMounted, setIsMounted] = useState(true);
  const mounted = useMounted();

  const { data, isError, isSuccess, isLoading, fetchStatus, isRefetching } =
    useGetImmunizationsForPatient({
      patientId: patientId as string,
      fastConsultationOnly: false,
      pagination: {
        page: tablePagination.page,
        pageSize: tablePagination.pageSize,
      },
      search: debounced.toLocaleLowerCase(),
    });

  const immunizationColumns = useMemo(
    () => [
      columnHelper.accessor("medication", {
        header: t("medication"),
        cell: info => {
          return (
            <p className="type-wrapper">
              {info.row.original.files?.length ? (
                <GrAttachment size={14} />
              ) : null}
              {truncateWithEllipsis(info.cell.getValue(), 20)}
            </p>
          );
        },
      }),
      columnHelper.accessor("immunizationDate", {
        header: t("date"),
        cell: info => {
          return (
            <Stack gap={5}>
              <Text fz={14}>
                {formatDateToString(info.cell.getValue() as Date)}
              </Text>
              <Text c="dimmed" fz={13}>
                {(info.cell.getValue() as Date).toLocaleTimeString(
                  i18n.language,
                  {
                    hour: "2-digit",
                    minute: "2-digit",
                  }
                )}
              </Text>
            </Stack>
          );
        },
      }),
      columnHelper.accessor("doses", {
        header: t("dosage"),
        cell: info => truncateWithEllipsis(info.row.original.doses, 15),
      }),
      columnHelper.accessor("administeredBy", {
        header: t("administered-by"),
        cell: info => truncateWithEllipsis(info.cell.getValue()?.fullName, 15),
      }),
      columnHelper.display({
        id: "action",
        header: isMobile ? t("action") : t("last-updated-action"),
        cell: info => {
          // console.log(info.row.original);
          return <TableControls immunization={info.row.original} />;
        },
      }),
    ],
    [tableData, i18n.language]
  );

  const table = useReactTable({
    data: tableData,
    columns: immunizationColumns,
    // state: {
    //   globalFilter: searchFilter,
    // },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    // globalFilterFn: (
    //   row: Row<Partial<Immunization>>,
    //   columnId: string,
    //   value: string
    // ) => {
    //   if (columnId === "medication") {
    //     return filterFns.includesString(row, columnId, value, () => null);
    //   }
    //   return false;
    // },
  });

  useEffect(() => {
    if (data) {
      setTableData(data.newData);
      setTablePagination(data.pagination);
    } else {
      setTableData([]);
    }
  }, [data]);

  useEffect(() => {
    if (setIsRefreshing) setIsRefreshing(isRefetching);
    if (mounted && isRefetching) return;
    if (mounted && !isRefetching) {
      setIsMounted(false);
    }
  }, [isRefetching, mounted]);

  return (
    <>
      <LoadingOverlay
        visible={isRefetching && !!data?.newData?.length && !isMounted}
        zIndex={1000}
        overlayProps={{ blur: 0.1, opacity: 0.6 }}
        loaderProps={{ display: "none" }}
      />
      <div className="filters-wrapper">
        <TextInput
          placeholder={t("search-for-record")}
          w={{ base: 300, md: 350, lg: 400 }}
          value={searchFilter}
          onChange={e =>
            setSearchFilter(
              e.currentTarget.value.toLocaleLowerCase(),
              "replace"
            )
          }
          leftSection={<IconSearch size={16} />}
          rightSection={
            searchFilter && (
              <ActionIcon
                variant="subtle"
                color="gray"
                onClick={() => setSearchFilter(null)}>
                <IconX size={16} />
              </ActionIcon>
            )
          }
        />
      </div>
      <StyledGeneralTabTable>
        <thead>
          {table.getHeaderGroups().map(headerGroup => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map(header => (
                <th key={header.id}>
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                </th>
              ))}
            </tr>
          ))}
        </thead>

        {isSuccess && !data?.newData?.length && debounced ? (
          <tbody>
            <tr className="helper-row">
              <td colSpan={immunizationColumns.length}>
                <p>
                  {t("empty-results-for", {
                    searchFilter: `"${debounced}"`,
                  })}
                </p>
              </td>
            </tr>
          </tbody>
        ) : null}

        {isSuccess && !data?.newData?.length && !debounced ? (
          <tbody>
            <tr className="helper-row">
              <td colSpan={immunizationColumns.length}>
                <p>{t("empty-immunizations")}</p>
              </td>
            </tr>
          </tbody>
        ) : null}

        {isLoading && fetchStatus === "fetching" && !tableData.length ? (
          <tbody>
            <tr className="helper-row">
              <td colSpan={immunizationColumns.length}>
                <p>{t("loading")}</p>
              </td>
            </tr>
          </tbody>
        ) : null}

        {isError && !data?.newData?.length ? (
          <tbody>
            <tr className="helper-row">
              <td colSpan={immunizationColumns.length}>
                <p>{t("encountered-error")}</p>
              </td>
            </tr>
          </tbody>
        ) : null}

        <tbody>
          {table.getRowModel().rows.map(row => (
            <tr key={row.id}>
              {row.getVisibleCells().map(cell => (
                <td key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </StyledGeneralTabTable>

      {(isLoading && fetchStatus === "fetching") || !data ? null : (
        <CustomPagination
          table={table}
          tablePagination={tablePagination}
          otherParams={{
            ...(debounced && { [QP.SEARCH]: debounced }),
            [QP.TAB]: "general",
          }}
        />
      )}
    </>
  );
};
